import { useCallback, useEffect, useMemo, useState } from 'react'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'

import { StatsTableCard, SummaryChartCard } from '~/analytics/containers/dashboard-components'
import DateSelectButton from '~/oakra/components/date-select-button'
import { useAppSelector } from '~/store'
import {
    abbreviateNumber,
    daysInRange,
    getChangePercent,
    getCurrentPeriodData,
    getOverallAOV,
    getPreviousPeriodData,
    getTotalNumSales,
    getTotalRevenue,
} from '~/tools/analytics'
import { getTranslation } from '~/tools/utils'
import { getPath } from '~/utils'

const useStyles = makeStyles({
    title: {
        fontSize: '1.5rem',
        marginBottom: 15,
    },
    controls: {
        marginBottom: 20,
    },
    row: {
        display: 'flex',
        marginBottom: 15,
    },
})

const getTotalOrdersData = (data) => {
    return data.map((d) => ({
        x: moment(d.date).toDate(),
        y: parseFloat(d.num_sales),
        label: abbreviateNumber(parseFloat(d.num_sales), 1),
    }))
}

const filterByStatus = (data, status) => {
    return data.filter((d) => d.status === status)
}

const getCountAndRate = (data, status) => {
    let statusCount = 0
    let totalCount = 0
    data.map((d) => {
        if (d.status === status) {
            statusCount += d.num_sales
        }
        totalCount += d.num_sales
    })
    return [statusCount, totalCount > 0 ? Math.round((statusCount * 100) / totalCount) / 10 : 0, 1]
}

export default function Dashboard() {
    const language = useAppSelector((state) => state.initial.language)
    moment.locale(language)

    const accountOwnerID = useAppSelector((state) => state.initial.accountOwner.id)
    const {
        analyticsTimeSeriesChartAPIURL,
        shopsRevenueSummaryAPIURL,
        channelsRevenueSummaryAPIURL,
        productsRevenueSummaryAPIURL,
        saleByStatusStatsAPIURL,
        fulfillmentByStatusStatsAPIURL,
    } = useAppSelector((state) => state.initial.endpoints)

    const classes = useStyles()

    const [isSummaryStatsLoaded, setIsSummaryStatsLoaded] = useState(false)
    const [summaryEndDate, setSummaryEndDate] = useState(moment().subtract(1, 'days'))
    const [summaryStartDate, setSummaryStartDate] = useState(moment().subtract(7, 'days'))

    const [summaryData, setSummaryData] = useState([])
    const [previousPeriodSummaryData, setPreviousPeriodSummaryData] = useState([])

    const [isSaleByStatusStatsLoaded, setIsSaleByStatusStatsLoaded] = useState(false)
    const [saleByStatusStats, setSaleByStatusStats] = useState([])

    const [isFulfillmentByStatusStatsLoaded, setIsFulfillmentByStatusStatsLoaded] = useState(false)
    const [fulfillmentByStatusStats, setFulfillmentByStatusStats] = useState([])

    const validOrderData = filterByStatus(summaryData, 'valid')
    const canceledOrderData = filterByStatus(summaryData, 'canceled')
    const returnedOrderData = filterByStatus(summaryData, 'returned')

    const previousValidOrderData = filterByStatus(previousPeriodSummaryData, 'valid')
    const previousCanceledOrderData = filterByStatus(previousPeriodSummaryData, 'canceled')
    const previousReturnedOrderData = filterByStatus(previousPeriodSummaryData, 'returned')

    // valid orders chart data
    const totalRevenueData = useMemo(() => {
        return validOrderData.map((d) => ({
            x: moment(d.date).toDate(),
            y: parseFloat(d.revenue),
            label: abbreviateNumber(parseFloat(d.revenue), 1),
        }))
    }, [validOrderData])

    const totalOrdersData = useMemo(() => {
        return getTotalOrdersData(validOrderData)
    }, [validOrderData])

    const AOVData = useMemo(() => {
        return validOrderData.map((d) => {
            const aov = d.num_sales > 0 ? parseFloat(d.revenue) / d.num_sales : 0
            const roundedAOV = Math.round(aov * 100) / 100

            return {
                x: moment(d.date).toDate(),
                y: roundedAOV,
                label: abbreviateNumber(roundedAOV, 1),
            }
        })
    }, [validOrderData])

    const previousAOVData = useMemo(() => {
        return previousValidOrderData.map((d) => {
            const aov = d.num_sales > 0 ? parseFloat(d.revenue) / d.num_sales : 0
            const roundedAOV = Math.round(aov * 100) / 100

            return {
                x: moment(d.date).toDate(),
                y: roundedAOV,
                label: abbreviateNumber(roundedAOV, 1),
            }
        })
    }, [previousValidOrderData])

    const previousTotalRevenueData = useMemo(() => {
        return previousValidOrderData.map((d) => ({
            x: moment(d.date).toDate(),
            y: parseFloat(d.revenue),
            label: abbreviateNumber(parseFloat(d.revenue), 1),
        }))
    }, [previousValidOrderData])

    const previousTotalOrdersData = useMemo(() => {
        return getTotalOrdersData(previousValidOrderData)
    }, [previousValidOrderData])

    // canceled chart data
    const totalCanceledOrdersData = useMemo(() => {
        return getTotalOrdersData(canceledOrderData)
    }, [canceledOrderData])

    const previousTotalCanceledOrdersData = useMemo(() => {
        return getTotalOrdersData(previousCanceledOrderData)
    }, [previousCanceledOrderData])

    const [totalCanceledOrdersNumber, totalCanceledOrdersRate] = useMemo(() => {
        return getCountAndRate(summaryData, 'canceled')
    }, [summaryData])

    const [previousTotalCanceledOrdersNumber, _previousTotalCanceledOrdersRate] = useMemo(() => {
        return getCountAndRate(previousPeriodSummaryData, 'canceled')
    }, [previousPeriodSummaryData])

    // returned chart data
    const totalReturnedOrdersData = useMemo(() => {
        return getTotalOrdersData(returnedOrderData)
    }, [returnedOrderData])

    const previousTotalReturnedOrdersData = useMemo(() => {
        return getTotalOrdersData(previousReturnedOrderData)
    }, [previousReturnedOrderData])

    const [totalReturnedOrdersNumber, totalReturnedOrdersRate] = useMemo(() => {
        return getCountAndRate(summaryData, 'returned')
    }, [summaryData])

    const [previousTotalReturnedOrdersNumber, _previousTotalReturnedOrdersRate] = useMemo(() => {
        return getCountAndRate(previousPeriodSummaryData, 'returned')
    }, [previousPeriodSummaryData])

    const fetchSummary = useCallback(async () => {
        const days = daysInRange(summaryStartDate, summaryEndDate)
        const startDate = summaryStartDate.clone().subtract(days, 'days').format('Y-MM-DD')
        const endDate = summaryEndDate.clone().format('Y-MM-DD')
        const urlWithParam = `${analyticsTimeSeriesChartAPIURL}?entity=account&entity_id=${accountOwnerID}&start_date=${startDate}&end_date=${endDate}`

        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }
        const data = await res.json()

        setIsSummaryStatsLoaded(true)

        setSummaryData(getCurrentPeriodData(summaryStartDate, summaryEndDate, data))
        setPreviousPeriodSummaryData(
            getPreviousPeriodData(summaryStartDate, summaryEndDate, data, true)
        )
    }, [analyticsTimeSeriesChartAPIURL, accountOwnerID, summaryStartDate, summaryEndDate])

    const fetchSaleByStatusStats = useCallback(async () => {
        const startDate = summaryStartDate.startOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const endDate = summaryEndDate.endOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const urlWithParam = `${saleByStatusStatsAPIURL}?start_date=${startDate}&end_date=${endDate}`
        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }

        const data = await res.json()
        setIsSaleByStatusStatsLoaded(true)
        setSaleByStatusStats(data)
    }, [saleByStatusStatsAPIURL, summaryStartDate, summaryEndDate])

    const fetchFulfillmentByStatusStats = useCallback(async () => {
        const startDate = summaryStartDate.startOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const endDate = summaryEndDate.endOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const urlWithParam = `${fulfillmentByStatusStatsAPIURL}?start_date=${startDate}&end_date=${endDate}`
        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }

        const data = await res.json()
        setIsFulfillmentByStatusStatsLoaded(true)
        setFulfillmentByStatusStats(data)
    }, [fulfillmentByStatusStatsAPIURL, summaryStartDate, summaryEndDate])

    const [revenueByShop, setRevenueByShop] = useState([])
    const revenueByShopData = useMemo(() => {
        return revenueByShop.map((d) => ({
            label: d.shop.shop_name,
            value: d.total_revenue,
            iconURL: d.shop.channel.channel_square_icon_url,
        }))
    }, [revenueByShop])

    const fetchRevenueByShop = useCallback(async () => {
        const startDate = summaryStartDate.startOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const endDate = summaryEndDate.endOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const urlWithParam = `${shopsRevenueSummaryAPIURL}?status=valid&start_date=${startDate}&end_date=${endDate}`
        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }

        const revenueByShopData = await res.json()
        setRevenueByShop(revenueByShopData)
    }, [shopsRevenueSummaryAPIURL, summaryStartDate, summaryEndDate])

    const [revenueByChannel, setRevenueByChannel] = useState([])
    const revenueByChannelData = useMemo(() => {
        return revenueByChannel.map((d) => ({
            label: d.channel.name,
            value: d.total_revenue,
            iconURL: d.channel.channel_square_icon_url,
        }))
    }, [revenueByChannel])

    const fetchRevenueByChannel = useCallback(async () => {
        const startDate = summaryStartDate.startOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const endDate = summaryEndDate.endOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const urlWithParam = `${channelsRevenueSummaryAPIURL}?status=valid&start_date=${startDate}&end_date=${endDate}`

        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }

        const revenueByChannelData = await res.json()
        setRevenueByChannel(revenueByChannelData)
    }, [channelsRevenueSummaryAPIURL, summaryStartDate, summaryEndDate])

    const [revenueByProduct, setRevenueByProduct] = useState([])
    const fetchRevenueByProductData = useCallback(async () => {
        const startDate = summaryStartDate.startOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const endDate = summaryEndDate.endOf('day').format('Y-MM-DDTHH:mm:ssZ')
        const urlWithParam = `${productsRevenueSummaryAPIURL}?status=valid&start_date=${startDate}&end_date=${endDate}`

        const res = await fetch(urlWithParam)
        if (!res.ok) {
            throw new Error(res.statusText)
        }

        setRevenueByProduct(await res.json())
    }, [productsRevenueSummaryAPIURL, summaryStartDate, summaryEndDate])

    const revenueByProductData = useMemo(() => {
        return revenueByProduct.map((d) => ({
            label: getTranslation(d.product, language, 'name'),
            value: d.total_revenue,
        }))
    }, [revenueByProduct, language])

    const saleBySatusStatsData = useMemo(() => {
        return saleByStatusStats
            .filter((d) => d.status !== 'unpaid')
            .map((d) => ({
                label: d.label,
                value: d.num_sales,
            }))
    }, [saleByStatusStats])

    const fulfillmentBySatusStatsData = useMemo(() => {
        return fulfillmentByStatusStats.map((d) => ({
            label: d.label,
            value: d.num_fulfillments,
        }))
    }, [fulfillmentByStatusStats])

    const handleDateChange = useCallback((startDate, endDate) => {
        setSummaryStartDate(startDate)
        setSummaryEndDate(endDate)
    }, [])

    useEffect(() => {
        fetchSummary()
        // fetchSummaryByStatus()
        fetchRevenueByShop()
        fetchRevenueByChannel()
        fetchRevenueByProductData()
        fetchSaleByStatusStats()
        fetchFulfillmentByStatusStats()
    }, [
        fetchSummary,
        // fetchSummaryByStatus,
        fetchRevenueByShop,
        fetchRevenueByChannel,
        fetchRevenueByProductData,
        fetchSaleByStatusStats,
        fetchFulfillmentByStatusStats,
    ])

    const currencySymbol = useAppSelector((state) => state.initial.currencySymbol)
    const daysInPeriod = useMemo(
        () => daysInRange(summaryStartDate, summaryEndDate),
        [summaryStartDate, summaryEndDate]
    )

    return (
        <Box
            sx={{
                overflowX: 'hidden',
                overflowY: 'auto',
                height: `calc(var(--app-height) - var(--app-toolbar-height) - var(--app-padding) * 2)`, // minus vertical padding
            }}
        >
            <Typography className={classes.title}>{gettext('Overview')}</Typography>
            <div className={classes.controls}>
                <DateSelectButton
                    startDate={summaryStartDate}
                    endDate={summaryEndDate}
                    handleDateChange={handleDateChange}
                />
            </div>
            <Grid container spacing={1}>
                <Grid item xs={6} md={4} lg={4}>
                    <SummaryChartCard
                        title={gettext('Total revenue')}
                        statTotal={`${currencySymbol}${Math.round(
                            getTotalRevenue(summaryData)
                        ).toLocaleString()}`}
                        change={getChangePercent(
                            getTotalRevenue(summaryData),
                            getTotalRevenue(previousPeriodSummaryData)
                        )}
                        data={totalRevenueData}
                        previousPeriodData={previousTotalRevenueData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&data_type=revenue`}
                        daysInPeriod={daysInPeriod}
                        loading={!isSummaryStatsLoaded}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <SummaryChartCard
                        title={gettext('Total orders')}
                        statTotal={getTotalNumSales(summaryData).toLocaleString()}
                        change={getChangePercent(
                            getTotalNumSales(summaryData),
                            getTotalNumSales(previousPeriodSummaryData)
                        )}
                        data={totalOrdersData}
                        previousPeriodData={previousTotalOrdersData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&data_type=num_sales`}
                        daysInPeriod={daysInPeriod}
                        loading={!isSummaryStatsLoaded}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <SummaryChartCard
                        title={gettext('Average order value')}
                        statTotal={`${currencySymbol}${getOverallAOV(summaryData).toLocaleString()}`}
                        change={getChangePercent(
                            getOverallAOV(summaryData),
                            getOverallAOV(previousPeriodSummaryData)
                        )}
                        data={AOVData}
                        previousPeriodData={previousAOVData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&data_type=aov`}
                        daysInPeriod={daysInPeriod}
                        loading={!isSummaryStatsLoaded}
                    />
                </Grid>

                <Grid item xs={6} md={4} lg={4}>
                    <StatsTableCard
                        cardTitle={gettext('Revenue by shop')}
                        labelTitle={gettext('Shop')}
                        valueTitle={interpolate(gettext('Revenue (%s)'), [currencySymbol])}
                        data={revenueByShopData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&group_by=shop`}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <StatsTableCard
                        cardTitle={gettext('Revenue by channel')}
                        labelTitle={gettext('Channel')}
                        valueTitle={interpolate(gettext('Revenue (%s)'), [currencySymbol])}
                        data={revenueByChannelData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&group_by=channel`}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <StatsTableCard
                        cardTitle={gettext('Top selling products')}
                        labelTitle={gettext('Product')}
                        valueTitle={interpolate(gettext('Revenue (%s)'), [currencySymbol])}
                        data={revenueByProductData}
                        reportLink={`${getPath('dashboardReports')}?status=valid&group_by=product`}
                    />
                </Grid>

                <Grid item xs={6} md={4} lg={4}>
                    <SummaryChartCard
                        title={gettext('Canceled orders')}
                        statTotal={`${totalCanceledOrdersNumber.toLocaleString()} (${totalCanceledOrdersRate.toLocaleString()}%)`}
                        change={getChangePercent(
                            totalCanceledOrdersNumber,
                            previousTotalCanceledOrdersNumber
                        )}
                        data={totalCanceledOrdersData}
                        previousPeriodData={previousTotalCanceledOrdersData}
                        reportLink={`${getPath('dashboardReports')}?status=canceled&data_type=num_sales`}
                        daysInPeriod={daysInPeriod}
                        loading={!isSummaryStatsLoaded}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <SummaryChartCard
                        title={gettext('Returned orders')}
                        statTotal={`${totalReturnedOrdersNumber.toLocaleString()} (${totalReturnedOrdersRate.toLocaleString()}%)`}
                        change={getChangePercent(
                            totalReturnedOrdersNumber,
                            previousTotalReturnedOrdersNumber
                        )}
                        data={totalReturnedOrdersData}
                        previousPeriodData={previousTotalReturnedOrdersData}
                        reportLink={`${getPath('dashboardReports')}?status=returned&data_type=num_sales`}
                        daysInPeriod={daysInPeriod}
                        loading={!isSummaryStatsLoaded}
                    />
                </Grid>
                <Grid item xs={6} md={4} lg={4}>
                    <StatsTableCard
                        cardTitle={gettext('Sales by status')}
                        labelTitle={gettext('Status')}
                        valueTitle={gettext('Sales')}
                        data={saleBySatusStatsData}
                        loading={!isSaleByStatusStatsLoaded}
                    />
                </Grid>

                <Grid item xs={6} md={4} lg={4}>
                    <StatsTableCard
                        cardTitle={gettext('Fulfillments by status')}
                        labelTitle={gettext('Status')}
                        valueTitle={gettext('Fulfillments')}
                        data={fulfillmentBySatusStatsData}
                        loading={!isFulfillmentByStatusStatsLoaded}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}
